import useSWR from 'swr';
import { BasketQuote, QuoteResponse } from '~/lib/types/basket-types';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { useEffect, useMemo } from 'react';
import { QUOTE_API } from '~/lib/constants';
import { useAuth } from 'react-oidc-context';
import { capturePromiseException } from '~/lib/metrics';

type UseQuotesParams = {
  origin: string;
  destination: string;
  parcels: {
    weight: number;
    height?: number | null;
    length?: number | null;
    width?: number | null;
    value?: number | null;
  }[];
  originPostcode?: string;
  destinationPostcode?: string;
  slug?: string;
  quoteType?: 'Default' | 'SameDay';
};

export default function useQuotes({
  origin,
  destination,
  parcels,
  originPostcode,
  destinationPostcode,
  slug,
  quoteType
}: UseQuotesParams) {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const { track } = useAnalytics();

  const id = useMemo(() => {
    const query = new URLSearchParams();

    if (parcels) {
      parcels?.forEach(({ weight, height, length, width }) => {
        query.append(`weight`, weight.toFixed(2));
        query.append(`quantity`, '1');
        if (height) query.append(`height`, height.toFixed(0));
        if (length) query.append(`length`, length.toFixed(0));
        if (width) query.append(`width`, width.toFixed(0));
      });
    } else {
      query.set(`weight`, '1');
      query.set(`quantity`, '1');
    }

    const value = parcels?.reduce((acc, parcel) => acc + (parcel.value || 0), 0);

    if (value && value > 0) query.set('valueInUnits', (value * 100).toFixed(0));
    if (originPostcode) query.set('originPostcode', originPostcode);
    if (destinationPostcode) query.set('destinationPostcode', destinationPostcode);
    if (slug) query.set('serviceSlugs', slug);

    if (quoteType) {
      if (quoteType === 'SameDay') {
        query.set('includeTags', 'SameDay');
      } else {
        query.set('excludeTags', 'SameDay');
      }
    }

    return `/quote/shipments/${origin}/${destination}?${query.toString()}`;
  }, [destination, destinationPostcode, parcels, origin, originPostcode, slug, quoteType]);

  const { data, error, isLoading } = useSWR<QuoteResponse>(
    `${QUOTE_API}${id}`,
    (u: string) =>
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, u)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const quotes: BasketQuote[] = useMemo(() => {
    if (data?.result) {
      return data.result.map((q) => {
        return { ...q, currency: data.currency };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (quotes && quotes.length > 0) {
      track({ type: 'quote_fetch', payload: { id, quotes: quotes }, persist: false });
    }
  }, [quotes, track, id]);

  return {
    quotes,
    loading: isLoading,
    error,
    id
  };
}
